import { useEffect, useRef, useState } from "react";
import {
  ChannelList,
  ChannelSettings,
  MessageSearch,
  useSendbird,
} from "@sendbird/uikit-react";
import ChannelWrapper from "./ChannelWrapper";
import Icon from "@/src/components/atom/Icon";
import ChatSvg from "@/src/assets/icons/floatingChat.svg";
import DeleteSvg from "@/src/assets/icons/icon-cancel-white.svg";
import ChatIconUserSvg from "@/src/assets/icons/chat-icon-user.svg";
import ChatIconBroadcastSvg from "@/src/assets/icons/chat-icon-broadcast.svg";
import styled, { createGlobalStyle } from "styled-components";
import colorSet from "@/src/styles/color";
import "@sendbird/uikit-react/dist/index.css";
import type { User } from "@sendbird/chat";
import type { GroupChannel, Member } from "@sendbird/chat/groupChannel";
import type { BaseMessage } from "@sendbird/chat/message";
import EditProfileModal from "./EditProfileModal";
import EditChannelInformationModal from "./EditChannelInformationModal";
import UserProfileCard from "./UserProfileCard";
import ChatInitializationStatus from "./ChatInitializationStatus";
import { useChatDraft } from "@/src/hooks/useChatDraft";
import { useSendbirdDomObserver } from "@/src/hooks/useSendbirdDomObserver";
import { useUpdateSendbirdUserMutation } from "@/src/store/apis/sendbird";
import { useAppDispatch, useAppSelector } from "@/src/store";
import {
  setCurrentChannelUrl,
  restoreSessionChatState,
} from "@/src/store/slice/chat";
import zIndex from "@/src/styles/zIndex";
import { useChannelListTimeFormatter } from "@/utils/dateFormatUtils";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import "dayjs/locale/ko";
import "dayjs/locale/ja";
import "dayjs/locale/zh-cn";
import "dayjs/locale/zh-tw";
import "dayjs/locale/en";
import { useTranslation } from "react-i18next";
import {
  saveChatStateToSession,
  loadChatStateFromSession,
  clearChatStateFromSession,
} from "@/src/utils/chatSessionStorage";

dayjs.extend(utc);
dayjs.extend(timezone);

const MessageSearchWithScroll = ({
  channelUrl,
  onClose,
  onUserProfileClick,
  authUser,
  setCurrentUser,
  setEditProfileModalVisible,
}: {
  channelUrl: string;
  onClose: () => void;
  onUserProfileClick: (user: User, position: { x: number; y: number }) => void;
  authUser: any;
  setCurrentUser: (user: User) => void;
  setEditProfileModalVisible: (visible: boolean) => void;
}) => {
  const { i18n } = useTranslation("chat");

  const handleResultClick = (message: BaseMessage) => {
    let messageElement: HTMLElement | null = null;

    messageElement = document.querySelector(
      `[data-sb-message-id="${message.messageId}"]`,
    );

    if (!messageElement) {
      const timeElements = Array.from(
        document.querySelectorAll(
          ".sendbird-message-content__middle__body-container__created-at",
        ),
      );
      for (const el of timeElements) {
        const senderTimezone = message.sender?.metaData?.timezone;
        let messageTime;
        try {
          const localeMap: { [key: string]: string } = {
            ko: "ko",
            ja: "ja",
            en: "en",
            "zh-CN": "zh-cn",
            "zh-TW": "zh-tw",
          };
          const currentLocale = localeMap[i18n.language] || "en";
          const time = senderTimezone
            ? dayjs(message.createdAt).tz(senderTimezone).locale(currentLocale)
            : dayjs(message.createdAt).locale(currentLocale);
          messageTime = time.format("h:mm A");
        } catch (error) {
          const localeMap: { [key: string]: string } = {
            ko: "ko",
            ja: "ja",
            en: "en",
            "zh-CN": "zh-cn",
            "zh-TW": "zh-tw",
          };
          const currentLocale = localeMap[i18n.language] || "en";
          messageTime = dayjs(message.createdAt)
            .locale(currentLocale)
            .format("h:mm A");
        }
        if (el.textContent?.includes(messageTime)) {
          messageElement = el.closest(".sendbird-message-hoc__container");
          break;
        }
      }
    }

    if (!messageElement && message.message) {
      const messages = Array.from(
        document.querySelectorAll(".sendbird-user-message__body"),
      );
      for (const msg of messages) {
        if (msg.textContent === message.message) {
          messageElement = msg.closest(".sendbird-message-hoc__container");
          break;
        }
      }
    }

    if (messageElement) {
      messageElement.scrollIntoView({
        behavior: "smooth",
        block: "center",
      });

      messageElement.classList.add("sendbird-message-highlight");
      setTimeout(() => {
        messageElement?.classList.remove("sendbird-message-highlight");
      }, 800); // Match bounce animation duration
    }
  };

  return (
    <MessageSearch
      channelUrl={channelUrl}
      onCloseClick={onClose}
      onResultClick={handleResultClick}
      renderSearchItem={({ message, onResultClick }) => {
        // 검색어 하이라이트 처리
        const HighlightedText = ({
          text,
          keyword,
        }: {
          text: string;
          keyword: string;
        }) => {
          if (!keyword || !text) return <>{text}</>;

          const regex = new RegExp(`(${keyword})`, "gi");
          const parts = text.split(regex);

          return (
            <>
              {parts.map((part, index) => {
                if (part.toLowerCase() === keyword.toLowerCase()) {
                  return (
                    <strong
                      key={index}
                      style={{ color: "#166DD7", fontWeight: 700 }}
                    >
                      {part}
                    </strong>
                  );
                }
                return part;
              })}
            </>
          );
        };

        // 검색어 추출 - 여러 선택자 시도
        const getSearchKeyword = () => {
          const selectors = [
            ".sendbird-message-search__input__input-field",
            ".sendbird-message-search__input input",
            "input[placeholder*='검색']",
            "input[placeholder*='Search']",
          ];

          for (const selector of selectors) {
            const input = document.querySelector(selector) as HTMLInputElement;
            if (input && input.value) {
              return input.value;
            }
          }
          return "";
        };

        const senderName = message.sender?.nickname || "Unknown";
        const messageText = message.message || "";
        const searchKeyword = getSearchKeyword();

        return (
          <div
            className="sendbird-message-search-item"
            onClick={() => onResultClick(message)}
            style={{
              padding: "16px",
              cursor: "pointer",
              borderBottom: "1px solid rgba(0,0,0,0.12)",
              transition: "background-color 0.2s",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = "#F8F8F8";
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = "transparent";
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
              <div
                className="sendbird-avatar"
                style={{
                  width: "36px",
                  height: "56px",
                  flexShrink: 0,
                  cursor: "default",
                  pointerEvents: "none",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                {message.sender?.profileUrl ? (
                  <img
                    src={message.sender.profileUrl}
                    alt={senderName}
                    style={{
                      width: "36px",
                      height: "36px",
                      borderRadius: "50%",
                      objectFit: "cover",
                    }}
                  />
                ) : (
                  <div
                    style={{
                      width: "36px",
                      height: "36px",
                      borderRadius: "50%",
                      backgroundColor: "#AEB7BC",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                    }}
                  >
                    <img
                      src={ChatIconUserSvg}
                      alt="Default"
                      style={{
                        width: "20px",
                        height: "20px",
                        filter: "brightness(0) invert(1)",
                      }}
                    />
                  </div>
                )}
              </div>

              <div
                style={{
                  flex: 1,
                  minWidth: 0,
                  height: "56px",
                  display: "flex",
                  flexDirection: "column",
                  justifyContent: "space-between",
                }}
              >
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <span
                    style={{
                      fontSize: "14px",
                      fontWeight: 500,
                      color: "rgba(0,0,0,0.88)",
                      fontFamily: "Roboto, sans-serif",
                      lineHeight: "16px",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                      flex: 1,
                      minWidth: 0,
                    }}
                  >
                    {senderName}
                  </span>
                  <span
                    style={{
                      fontSize: "12px",
                      color: "rgba(0,0,0,0.38)",
                      fontFamily: "Roboto, sans-serif",
                      lineHeight: "12px",
                      marginLeft: "8px",
                      flexShrink: 0,
                      whiteSpace: "nowrap",
                    }}
                  >
                    {(() => {
                      // localStorage에서 현재 언어 가져오기
                      const currentLang =
                        localStorage.getItem("i18nextLng") || "en";

                      // SendBird UIKit과 동일한 포맷 사용
                      const messageDate = new Date(message.createdAt);
                      const today = new Date();
                      const yesterday = new Date();
                      yesterday.setDate(yesterday.getDate() - 1);

                      // 날짜만 비교하기 위해 시간 제거
                      const messageDateOnly = new Date(
                        messageDate.getFullYear(),
                        messageDate.getMonth(),
                        messageDate.getDate(),
                      );
                      const todayDateOnly = new Date(
                        today.getFullYear(),
                        today.getMonth(),
                        today.getDate(),
                      );
                      const yesterdayDateOnly = new Date(
                        yesterday.getFullYear(),
                        yesterday.getMonth(),
                        yesterday.getDate(),
                      );

                      // 어제 표시 텍스트
                      const yesterdayText: { [key: string]: string } = {
                        en: "Yesterday",
                        ko: "어제",
                        ja: "昨日",
                        "zh-TW": "昨天",
                        "zh-CN": "昨天",
                      };

                      // 날짜 비교
                      if (
                        messageDateOnly.getTime() === todayDateOnly.getTime()
                      ) {
                        // 오늘: 시간만 표시
                        const hours = messageDate.getHours();
                        const minutes = messageDate.getMinutes();

                        // 언어별 시간 포맷 설정
                        const formatters: {
                          [key: string]: (h: number, m: number) => string;
                        } = {
                          en: (h, m) => {
                            const period = h >= 12 ? "PM" : "AM";
                            const hour12 = h % 12 || 12;
                            return `${hour12}:${m.toString().padStart(2, "0")} ${period}`;
                          },
                          ko: (h, m) => {
                            const period = h >= 12 ? "오후" : "오전";
                            const hour12 = h % 12 || 12;
                            return `${period} ${hour12}:${m.toString().padStart(2, "0")}`;
                          },
                          ja: (h, m) => {
                            const period = h >= 12 ? "午後" : "午前";
                            const hour12 = h % 12 || 12;
                            return `${period} ${hour12}:${m.toString().padStart(2, "0")}`;
                          },
                          "zh-TW": (h, m) => {
                            const period = h >= 12 ? "下午" : "上午";
                            const hour12 = h % 12 || 12;
                            return `${period} ${hour12}:${m.toString().padStart(2, "0")}`;
                          },
                          "zh-CN": (h, m) => {
                            const period = h >= 12 ? "下午" : "上午";
                            const hour12 = h % 12 || 12;
                            return `${period} ${hour12}:${m.toString().padStart(2, "0")}`;
                          },
                        };

                        const formatter =
                          formatters[currentLang] || formatters.en;
                        return formatter(hours, minutes);
                      } else if (
                        messageDateOnly.getTime() ===
                        yesterdayDateOnly.getTime()
                      ) {
                        // 어제
                        return yesterdayText[currentLang] || yesterdayText.en;
                      } else {
                        // 그 이전: YYYY-MM-DD 형식
                        const year = messageDate.getFullYear();
                        const month = (messageDate.getMonth() + 1)
                          .toString()
                          .padStart(2, "0");
                        const day = messageDate
                          .getDate()
                          .toString()
                          .padStart(2, "0");
                        return `${year}-${month}-${day}`;
                      }
                    })()}
                  </span>
                </div>

                <div
                  style={{
                    fontSize: "12px",
                    color: "rgba(0,0,0,0.38)",
                    lineHeight: "16px",
                    wordBreak: "break-word",
                    fontFamily: "Roboto, sans-serif",
                    height: "32px",
                    overflow: "hidden",
                    display: "-webkit-box",
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: "vertical",
                  }}
                >
                  <HighlightedText text={messageText} keyword={searchKeyword} />
                </div>
              </div>
            </div>
          </div>
        );
      }}
    />
  );
};

interface FloatingChatProps {
  isLoading?: boolean;
  error?: string | null;
  retryCount?: number;
  maxRetries?: number;
  onRetry?: () => void;
}

const FloatingChat: React.FC<FloatingChatProps> = ({
  isLoading = false,
  error = null,
  retryCount = 0,
  maxRetries = 3,
  onRetry,
}) => {
  const [showChannel, setShowChannel] = useState(false);
  const [currentChannel, setCurrentChannel] = useState<GroupChannel | null>(
    null,
  );
  const [isUpdatingChannel, setIsUpdatingChannel] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showMessageSearch, setShowMessageSearch] = useState(false);
  const [editProfileModalVisible, setEditProfileModalVisible] = useState(false);
  const [editChannelModalVisible, setEditChannelModalVisible] = useState(false);
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  const [userProfileCardVisible, setUserProfileCardVisible] = useState(false);
  const formatChannelListTime = useChannelListTimeFormatter();
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [userProfilePosition, setUserProfilePosition] = useState<
    { x: number; y: number } | undefined
  >();
  const [channelListKey, setChannelListKey] = useState(0);
  const [pendingChannelUrl, setPendingChannelUrl] = useState<string | null>(
    null,
  );

  const {
    state: { stores },
  } = useSendbird();
  const [updateSendbirdUser] = useUpdateSendbirdUserMutation();
  const { i18n } = useTranslation("chat");
  const authUser = useAppSelector((state) => state.auth.user);
  const reduxCurrentChannelUrl = useAppSelector(
    (state) => state.chat.currentChannelUrl,
  );
  const dispatch = useAppDispatch();

  // Custom hooks 사용
  const { saveDraftOnClose, deleteDraftFromLocalStorage } = useChatDraft(
    currentChannel,
    showChannel,
    isUpdatingChannel,
  );

  const { applyAllCustomizations } = useSendbirdDomObserver(
    currentChannel,
    showChannel,
    authUser,
  );

  // 현재 채널 URL ref (여전히 필요)
  const currentChannelUrlRef = useRef<string | null>(null);

  // SendBird SDK is accessed through stores when needed

  // Redux에서 unread count 가져오기
  const totalUnreadCount = useAppSelector(
    (state) => state.chat.totalUnreadCount,
  );

  // Draft functions are now provided by useChatDraft hook
  // The hook handles saveDraftToLocalStorage, deleteDraftFromLocalStorage, and restoreDraftFromLocalStorage

  // Debounced save is handled by the useChatDraft hook internally

  // Draft restoration is handled by the useChatDraft hook

  // Initialize session state restoration on component mount
  useEffect(() => {
    const savedChatState = loadChatStateFromSession();
    if (savedChatState && stores?.sdkStore?.sdk?.groupChannel) {
      // Restore saved channel state
      dispatch(
        restoreSessionChatState({
          currentChannelUrl: savedChatState.currentChannelUrl,
          currentChannel: savedChatState.currentChannel,
        }),
      );
      setPendingChannelUrl(savedChatState.currentChannelUrl);
    }
  }, [dispatch, stores?.sdkStore?.sdk]);

  // Handle session state restoration when chat panel reopens
  useEffect(() => {
    // Only restore when chat panel opens (showChannel becomes true)
    // and there's no current channel (to avoid overriding user's current selection)
    if (showChannel && !currentChannel && stores?.sdkStore?.sdk?.groupChannel) {
      const savedChatState = loadChatStateFromSession();
      if (savedChatState) {
        // Restore saved channel state
        dispatch(
          restoreSessionChatState({
            currentChannelUrl: savedChatState.currentChannelUrl,
            currentChannel: savedChatState.currentChannel,
          }),
        );
        setPendingChannelUrl(savedChatState.currentChannelUrl);
      }
    }
  }, [showChannel, currentChannel, dispatch, stores?.sdkStore?.sdk]);

  useEffect(() => {
    if (pendingChannelUrl && stores?.sdkStore?.sdk?.groupChannel) {
      stores.sdkStore.sdk.groupChannel
        .getChannel(pendingChannelUrl)
        .then((channel) => {
          setCurrentChannel(channel);
          setPendingChannelUrl(null);
        })
        .catch((err) => {
          // Failed to restore channel, clear saved state
          clearChatStateFromSession();
          setPendingChannelUrl(null);
        });
    }
  }, [channelListKey, pendingChannelUrl, stores?.sdkStore?.sdk]);

  useEffect(() => {
    if (!showChannel) {
      // Save current chat state to session storage before closing
      if (currentChannel && reduxCurrentChannelUrl) {
        saveChatStateToSession(reduxCurrentChannelUrl, currentChannel);
      }

      // 채팅창이 닫힐 때만 채널 초기화
      setCurrentChannel(null);
      setShowSettings(false);
      setShowMessageSearch(false);
      dispatch(setCurrentChannelUrl(null));

      // Draft 및 DOM observer 정리는 custom hooks에서 처리됨
    }
  }, [showChannel, dispatch, currentChannel, reduxCurrentChannelUrl]);

  // 채널 변경 시 처리
  useEffect(() => {
    if (!currentChannel?.url) return;

    // 현재 채널 URL ref 업데이트
    currentChannelUrlRef.current = currentChannel.url;

    // Redux에 현재 채널 저장
    dispatch(setCurrentChannelUrl(currentChannel.url));

    // Draft management is handled by useChatDraft hook
    // DOM customization is handled by useSendbirdDomObserver hook
  }, [currentChannel?.url, dispatch]);

  // beforeunload 이벤트 핸들러 - 채팅창을 닫을 때 draft 저장
  useEffect(() => {
    const handleBeforeUnload = () => {
      // saveDraftOnClose is provided by useChatDraft hook
      saveDraftOnClose();
    };

    window.addEventListener("beforeunload", handleBeforeUnload);

    return () => {
      window.removeEventListener("beforeunload", handleBeforeUnload);
    };
  }, [saveDraftOnClose]);

  useEffect(() => {
    if (editProfileModalVisible && stores?.sdkStore?.sdk?.currentUser) {
      const sdk = stores.sdkStore.sdk;
      const fetchLatestUserInfo = async () => {
        try {
          const reduxUser = authUser;

          if (sdk.currentUser && reduxUser) {
            const updatedMetaData = {
              ...(sdk.currentUser.metaData || {}),
              contact: reduxUser.personalContact
                ? `${reduxUser.personalContactPrefix || ""} ${reduxUser.personalContact}`.trim()
                : "-",
              email: reduxUser.email || reduxUser.aId || "",
              profileName:
                `${reduxUser.name || ""} (${reduxUser.exporter?.companyName || ""})`.trim(),
            };

            try {
              await sdk.updateCurrentUserInfo({
                metaData: updatedMetaData,
              });

              await sdk.connect(sdk.currentUser.userId);
            } catch (updateError) {
              // Failed to update Sendbird metadata
            }

            setCurrentUser({
              userId: sdk.currentUser.userId,
              nickname: sdk.currentUser.nickname,
              profileUrl: sdk.currentUser.profileUrl,
              metaData: updatedMetaData,
            });
          }
        } catch (error) {
          // Failed to refresh user info
        }
      };

      fetchLatestUserInfo();
    }
  }, [editProfileModalVisible, stores, authUser]);

  const withSidePanel = !!currentChannel && (showMessageSearch || showSettings);
  const onlyList = !currentChannel;

  useEffect(() => {
    if (showSettings && currentChannel && stores?.sdkStore?.sdk) {
      const handleEditModal = () => {
        const currentUserId = stores?.sdkStore?.sdk?.currentUser?.userId;

        // SendBird 기본 프로필 URL 체크 함수
        const isDefaultProfileUrl = (url: string) => {
          return (
            !url ||
            url.includes("profile_placeholder.png") ||
            url.includes("sample/cover")
          );
        };

        // 채널에 커스텀 이미지가 설정되어 있는지 확인
        const hasCustomImage =
          currentChannel.coverUrl && currentChannel.coverUrl !== "";

        // 현재 사용자와 봇을 제외한 다른 멤버들만 필터링
        // custom_type이 'contract'인 경우 operators도 제외
        const otherMembers = currentChannel.members.filter((member: Member) => {
          if (member.userId === "ecoya_export_bot") return false;
          if (member.userId === currentUserId) return false;

          // custom_type이 'contract'인 경우 operators 제외
          if (
            currentChannel.customType === "contract" &&
            currentChannel.operators
          ) {
            const isOperator = currentChannel.operators.some(
              (operator: Member) => operator.userId === member.userId,
            );
            if (isOperator) {
              return false;
            }
          }

          return true;
        });

        // Edit 모달의 아바타 찾기
        const modalRoot = document.querySelector(".sendbird-modal-root");
        if (!modalRoot) return;

        const editModalAvatar = modalRoot.querySelector(
          ".channel-profile-form__avatar .sendbird-avatar--inner__two-child",
        );
        if (editModalAvatar) {
          const parentAvatar = editModalAvatar.closest(".sendbird-avatar");
          if (parentAvatar && otherMembers.length === 1) {
            // 기존 two-child 내용 숨기기
            (editModalAvatar as HTMLElement).style.display = "none";

            // 이미 커스텀 아바타가 추가되었는지 확인
            if (!parentAvatar.querySelector(".custom-edit-avatar")) {
              const customAvatar = document.createElement("div");
              customAvatar.className = "custom-edit-avatar";
              customAvatar.style.width = "100%";
              customAvatar.style.height = "100%";
              customAvatar.style.position = "absolute";
              customAvatar.style.top = "0";
              customAvatar.style.left = "0";

              const imageUrl =
                hasCustomImage && !isDefaultProfileUrl(currentChannel.coverUrl)
                  ? currentChannel.coverUrl
                  : otherMembers[0].profileUrl;

              if (imageUrl && !isDefaultProfileUrl(imageUrl)) {
                const img = document.createElement("img");
                img.src = imageUrl;
                img.alt = otherMembers[0].nickname || "";
                img.style.width = "100%";
                img.style.height = "100%";
                img.style.borderRadius = "50%";
                img.style.objectFit = "cover";
                customAvatar.appendChild(img);
              } else {
                // 기본 아바타
                customAvatar.style.borderRadius = "50%";
                customAvatar.style.backgroundColor = colorSet.gray7;
                customAvatar.style.display = "flex";
                customAvatar.style.alignItems = "center";
                customAvatar.style.justifyContent = "center";

                const iconImg = document.createElement("img");
                iconImg.src = ChatIconUserSvg;
                iconImg.alt = otherMembers[0].nickname || "";
                iconImg.style.width = "44px";
                iconImg.style.height = "44px";
                iconImg.style.filter = "brightness(0) invert(1)";
                customAvatar.appendChild(iconImg);
              }

              parentAvatar.appendChild(customAvatar);
              (parentAvatar as HTMLElement).style.position = "relative";
            }
          }
        }
      };

      // Edit 버튼 클릭 감지
      const detectEditClick = (e: MouseEvent) => {
        const target = e.target as HTMLElement;
        const isEditButton =
          target.textContent?.includes("Edit") ||
          target.closest('[aria-label*="edit"]') ||
          target.closest(".sendbird-iconbutton--edit");

        if (isEditButton) {
          setTimeout(handleEditModal, 300);
          setTimeout(handleEditModal, 500);
          setTimeout(handleEditModal, 800);
        }

        // Save 버튼 클릭 감지
        const isSaveButton =
          target.tagName === "BUTTON" && target.textContent?.includes("Save");
        if (isSaveButton) {
          // 저장 후 채널 정보 업데이트
          setTimeout(() => {
            if (stores?.sdkStore?.sdk && currentChannel) {
              stores.sdkStore.sdk.groupChannel
                .getChannel(currentChannel.url)
                .then((updatedChannel: GroupChannel) => {
                  // 채널 정보가 변경되었으면 상태 업데이트
                  if (
                    updatedChannel.name !== currentChannel.name ||
                    updatedChannel.coverUrl !== currentChannel.coverUrl
                  ) {
                    setCurrentChannel(updatedChannel);

                    // ChannelList 강제 리렌더링
                    setChannelListKey((prev) => prev + 1);
                  }
                })
                .catch((err: Error) => {
                  // Failed to fetch updated channel
                });
            }
          }, 1000); // Save 처리 후 1초 대기
        }
      };

      document.addEventListener("click", detectEditClick, true);

      return () => {
        document.removeEventListener("click", detectEditClick, true);
      };
    }
  }, [showSettings, currentChannel, stores]);

  // 읽음 표시 아이콘 변경 처리
  useEffect(() => {
    if (currentChannel && showChannel) {
      const replaceReadReceipts = () => {
        // done-all (읽음) 아이콘을 done (전송) 아이콘으로 변경
        const doneAllIcons = document.querySelectorAll(
          ".sendbird-icon-done-all svg",
        );
        doneAllIcons.forEach((svg) => {
          const path = svg.querySelector("path");
          if (path) {
            // done 아이콘의 path 데이터로 변경
            path.setAttribute(
              "d",
              "M12.552 31.448a2.665 2.665 0 1 0-3.771 3.771l13.333 13.333a2.666 2.666 0 0 0 3.772 0L55.219 19.22a2.667 2.667 0 0 0-3.771-3.771L24 42.895z",
            );
          }
        });
      };

      // MutationObserver로 DOM 변경 감지
      const observer = new MutationObserver(() => {
        replaceReadReceipts();
      });

      // 채널 섹션 관찰
      const channelSection = document.getElementById("channel-section");
      if (channelSection) {
        observer.observe(channelSection, {
          childList: true,
          subtree: true,
          attributes: true,
          attributeFilter: ["class"],
        });

        // 초기 실행
        replaceReadReceipts();
      }

      // 주기적으로도 체크 (fallback)
      const interval = setInterval(replaceReadReceipts, 500);

      return () => {
        observer.disconnect();
        clearInterval(interval);
      };
    }
  }, [currentChannel, showChannel]);

  // Channel Information 패널의 sendbird-avatar--inner__two-child 클래스 처리
  useEffect(() => {
    if (showSettings && currentChannel && stores?.sdkStore?.sdk) {
      const updateChannelInfoAvatar = () => {
        // SendBird가 자동으로 추가하는 중복 아바타 제거
        const channelProfile = document.querySelector(
          ".sendbird-channel-profile",
        );
        if (channelProfile) {
          // 중복된 sendbird-chat-header--avatar--group-channel 제거
          const avatarContainers = channelProfile.querySelectorAll(
            ".sendbird-chat-header--avatar--group-channel",
          );
          if (avatarContainers.length > 1) {
            // 첫 번째를 제외한 나머지 제거
            for (let i = 1; i < avatarContainers.length; i++) {
              avatarContainers[i].remove();
            }
          }

          // sendbird-avatar 컨테이너 내부의 중복된 이미지 처리
          const sendbirdAvatars = channelProfile.querySelectorAll(
            ".sendbird-chat-header--avatar--group-channel .sendbird-avatar",
          );
          sendbirdAvatars.forEach((avatar) => {
            const images = avatar.querySelectorAll("img");
            if (images.length > 1) {
              // 두 개 이상의 이미지가 있으면 첫 번째만 남기고 제거
              for (let i = 1; i < images.length; i++) {
                images[i].remove();
              }
            }

            // two-child 클래스를 가진 컨테이너도 처리
            const twoChildContainers = avatar.querySelectorAll(
              ".sendbird-avatar--inner__two-child",
            );
            if (twoChildContainers.length > 1) {
              for (let i = 1; i < twoChildContainers.length; i++) {
                twoChildContainers[i].remove();
              }
            }
          });
        }
        const currentUserId = stores?.sdkStore?.sdk?.currentUser?.userId;

        // SendBird 기본 프로필 URL 체크 함수
        const isDefaultProfileUrl = (url: string) => {
          return (
            !url ||
            url.includes("profile_placeholder.png") ||
            url.includes("sample/cover")
          );
        };

        // 채널에 커스텀 이미지가 설정되어 있는지 확인
        const hasCustomImage =
          currentChannel.coverUrl && currentChannel.coverUrl !== "";

        // 현재 사용자와 봇을 제외한 다른 멤버들만 필터링
        // custom_type이 'contract'인 경우 operators도 제외
        const otherMembers = currentChannel.members.filter((member: Member) => {
          if (member.userId === "ecoya_export_bot") return false;
          if (member.userId === currentUserId) return false;

          // custom_type이 'contract'인 경우 operators 제외
          if (
            currentChannel.customType === "contract" &&
            currentChannel.operators
          ) {
            const isOperator = currentChannel.operators.some(
              (operator: Member) => operator.userId === member.userId,
            );
            if (isOperator) {
              return false;
            }
          }

          return true;
        });

        // 기존 커스텀 아바타 모두 제거
        const existingCustomAvatars = document.querySelectorAll(
          ".custom-channel-avatar",
        );
        existingCustomAvatars.forEach((avatar) => avatar.remove());

        // 모든 two-child 요소의 display 속성 리셋
        const allTwoChildElements = document.querySelectorAll(
          ".sendbird-avatar--inner__two-child",
        );
        allTwoChildElements.forEach((el) => {
          (el as HTMLElement).style.display = "";
        });

        // sendbird-avatar--inner__two-child 클래스를 가진 요소 찾기
        const twoChildAvatars = document.querySelectorAll(
          ".sendbird-avatar--inner__two-child",
        );

        twoChildAvatars.forEach((avatarContainer) => {
          // 기존 내용을 숨기고 새로운 아바타로 교체
          const parentAvatar = avatarContainer.closest(".sendbird-avatar");
          if (parentAvatar && otherMembers.length === 1) {
            // 기존 two-child 내용 숨기기
            (avatarContainer as HTMLElement).style.display = "none";

            // 이미 커스텀 아바타가 추가되었는지 확인
            if (!parentAvatar.querySelector(".custom-channel-avatar")) {
              const customAvatar = document.createElement("div");
              customAvatar.className = "custom-channel-avatar";
              customAvatar.style.width = "100%";
              customAvatar.style.height = "100%";
              customAvatar.style.position = "absolute";
              customAvatar.style.top = "0";
              customAvatar.style.left = "0";

              const imageUrl =
                hasCustomImage && !isDefaultProfileUrl(currentChannel.coverUrl)
                  ? currentChannel.coverUrl
                  : otherMembers[0].profileUrl;

              if (imageUrl && !isDefaultProfileUrl(imageUrl)) {
                const img = document.createElement("img");
                img.src = imageUrl;
                img.alt = otherMembers[0].nickname || "";
                img.style.width = "100%";
                img.style.height = "100%";
                img.style.borderRadius = "50%";
                img.style.objectFit = "cover";
                customAvatar.appendChild(img);
              } else {
                // 기본 아바타
                customAvatar.style.borderRadius = "50%";
                customAvatar.style.backgroundColor = colorSet.gray7;
                customAvatar.style.display = "flex";
                customAvatar.style.alignItems = "center";
                customAvatar.style.justifyContent = "center";

                const iconImg = document.createElement("img");
                iconImg.src = ChatIconUserSvg;
                iconImg.alt = otherMembers[0].nickname || "";
                iconImg.style.width = "44px";
                iconImg.style.height = "44px";
                iconImg.style.filter = "brightness(0) invert(1)";
                customAvatar.appendChild(iconImg);
              }

              parentAvatar.appendChild(customAvatar);
              (parentAvatar as HTMLElement).style.position = "relative";
            }
          }
        });

        // Channel Information 패널 내부의 채널명도 변경
        const channelNameElements = document.querySelectorAll(
          ".sendbird-channel-profile__title.sendbird-label.sendbird-label--subtitle-2.sendbird-label--color-onbackground-1",
        );
        channelNameElements.forEach((channelName) => {
          // custom_type이 'contract'인 경우 상대방의 profileName 표시
          if (
            currentChannel.customType === "contract" &&
            otherMembers.length > 0
          ) {
            channelName.textContent = otherMembers
              .map((m: Member) => m.metaData?.profileName || m.nickname)
              .filter((n: string) => n)
              .join(", ");
          } else {
            channelName.textContent = currentChannel.name || "Channel";
          }
        });
      };

      // MutationObserver로 DOM 변경 감지
      let isUpdating = false;
      const observer = new MutationObserver((mutations) => {
        // DOM 조작으로 인한 무한 루프 방지
        const shouldUpdate = mutations.some((mutation) => {
          // 우리가 추가한 요소의 변경은 무시
          if (
            mutation.target instanceof Element &&
            (mutation.target.classList.contains("custom-channel-avatar") ||
              mutation.target.closest(".custom-channel-avatar"))
          ) {
            return false;
          }
          return true;
        });

        if (shouldUpdate && !isUpdating) {
          isUpdating = true;
          updateChannelInfoAvatar();
          setTimeout(() => {
            isUpdating = false;
          }, 100);
        }
      });

      // Channel Settings 영역 관찰
      const settingsArea = document.querySelector(".sendbird-channel-settings");
      if (settingsArea) {
        observer.observe(settingsArea, {
          childList: true,
          subtree: true,
        });
      }

      // 초기 실행
      setTimeout(updateChannelInfoAvatar, 100);
      setTimeout(updateChannelInfoAvatar, 300);
      setTimeout(updateChannelInfoAvatar, 500);

      // Edit Channel Information 저장 후 추가 정리
      const detectChannelUpdate = () => {
        setTimeout(() => {
          updateChannelInfoAvatar();
          // 추가로 중복 제거 확실히 하기
          const channelProfile = document.querySelector(
            ".sendbird-channel-profile",
          );
          if (channelProfile) {
            const allAvatarContainers =
              channelProfile.querySelectorAll(".sendbird-avatar");
            allAvatarContainers.forEach((container) => {
              // 내부에 two-child가 여러 개 있으면 정리
              const innerContainers = container.querySelectorAll(
                ".sendbird-avatar--inner__two-child",
              );
              if (innerContainers.length > 1) {
                // 첫 번째를 제외한 나머지 제거
                for (let i = 1; i < innerContainers.length; i++) {
                  innerContainers[i].remove();
                }
              }

              // 중복 이미지도 정리
              const images = container.querySelectorAll("img");
              if (images.length > 2) {
                // 최대 2개까지만 허용 (two-child의 경우)
                for (let i = 2; i < images.length; i++) {
                  images[i].remove();
                }
              }
            });
          }
        }, 700);
      };

      // 채널 업데이트 감지를 위한 이벤트 리스너
      const channelUpdateObserver = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.target instanceof Element) {
            // 모달이 닫히는 것을 감지
            if (mutation.removedNodes.length > 0) {
              mutation.removedNodes.forEach((node) => {
                if (
                  node instanceof Element &&
                  (node.classList.contains("sendbird-modal") ||
                    node.querySelector(".sendbird-modal"))
                ) {
                  detectChannelUpdate();
                }
              });
            }
          }
        });
      });

      channelUpdateObserver.observe(document.body, {
        childList: true,
        subtree: true,
      });

      return () => {
        observer.disconnect();
        channelUpdateObserver.disconnect();
      };
    }
  }, [showSettings, currentChannel, stores]);

  // Bot 메시지 스타일링 및 DOM customization은 useSendbirdDomObserver hook에서 처리
  useEffect(() => {
    if (!showChannel || !currentChannel) return;

    // DOM customization is handled by useSendbirdDomObserver hook
    // The hook already handles bot message styling, channel avatars, timestamps, and read receipts
    applyAllCustomizations();
  }, [showChannel, currentChannel, applyAllCustomizations]);

  // Bot message styling is handled by useSendbirdDomObserver hook
  // Avatar replacement is handled by useSendbirdDomObserver hook

  // 프로필 클릭 이벤트 리스너 추가
  useEffect(() => {
    if (showChannel && stores?.sdkStore?.sdk) {
      const timer = setTimeout(() => {
        const handleProfileClick = (e: Event) => {
          const target = e.target as HTMLElement;

          // 채널 리스트 헤더의 내 프로필 영역 클릭
          const isMyProfileClick = target.closest(
            ".sendbird-channel-header__title",
          );

          if (isMyProfileClick) {
            const sdk = stores.sdkStore.sdk;
            const reduxUser = authUser;

            if (sdk && sdk.currentUser && reduxUser) {
              // Redux store의 최신 정보로 metaData 구성
              const updatedMetaData = {
                ...(sdk.currentUser.metaData || {}),
                contact: reduxUser.personalContact
                  ? `${reduxUser.personalContactPrefix || ""} ${reduxUser.personalContact}`.trim()
                  : "-",
                email: reduxUser.email || reduxUser.aId || "",
                profileName:
                  `${reduxUser.name || ""} (${reduxUser.exporter?.companyName || ""})`.trim(),
              };

              setCurrentUser({
                userId: sdk.currentUser.userId,
                nickname: sdk.currentUser.nickname,
                profileUrl: sdk.currentUser.profileUrl,
                metaData: updatedMetaData,
              });
              setEditProfileModalVisible(true);
            }
            return;
          }

          // 메시지의 프로필 클릭 (다른 사용자)
          const messageProfileElement =
            target.closest(
              ".sendbird-message-content__left .sendbird-avatar",
            ) ||
            target.closest(".sendbird-avatar") ||
            (target.tagName === "IMG" &&
              target.closest(".sendbird-message-content__left"));

          if (messageProfileElement) {
            e.preventDefault();
            e.stopPropagation();

            // 메시지 컨테이너 찾기 - 더 넓은 범위로 검색
            const messageContainer =
              messageProfileElement.closest(".sendbird-msg-hoc") ||
              messageProfileElement.closest(
                ".sendbird-message-hoc__container",
              ) ||
              messageProfileElement.closest("[class*='sendbird-message']");

            if (messageContainer) {
              let senderName = "";
              let profileUrl = "";
              let userId = "";

              // 1. 같은 메시지 그룹 내에서 sender name 찾기
              const senderNameEl =
                messageContainer.querySelector(".sendbird-label--caption-1") ||
                messageContainer.querySelector(
                  ".sendbird-message-content__middle__sender-name",
                ) ||
                messageContainer.querySelector("[class*='sender-name']");

              if (senderNameEl) {
                senderName = senderNameEl.textContent?.trim() || "";
              }

              // 2. 만약 못 찾았다면 위쪽 메시지들 확인 (같은 발신자의 연속 메시지일 경우)
              if (!senderName) {
                let prevElement = messageContainer.previousElementSibling;
                let searchCount = 0;
                while (prevElement && searchCount < 10) {
                  const prevNameEl =
                    prevElement.querySelector(".sendbird-label--caption-1") ||
                    prevElement.querySelector(
                      ".sendbird-message-content__middle__sender-name",
                    );
                  if (prevNameEl?.textContent) {
                    senderName = prevNameEl.textContent.trim();
                    break;
                  }
                  prevElement = prevElement.previousElementSibling;
                  searchCount++;
                }
              }

              // 3. 프로필 이미지 URL 추출
              const avatarImg =
                messageProfileElement.querySelector("img") ||
                messageProfileElement.closest("img");
              profileUrl = avatarImg?.src || "";

              // 4. userId는 senderName으로 대체
              userId = senderName || "user";

              if (senderName || profileUrl) {
                // 클릭된 아바타의 위치 계산
                const rect = messageProfileElement.getBoundingClientRect();
                const position = {
                  x: rect.left + rect.width / 2,
                  y: rect.top,
                };

                // Sendbird SDK를 통해 실제 사용자 정보 가져오기
                const fetchUserAndShow = async () => {
                  if (userId && stores?.sdkStore?.sdk) {
                    try {
                      // 모든 사용자를 가져오기 위해 페이지네이션 사용
                      const query =
                        stores.sdkStore.sdk.createApplicationUserListQuery();

                      // 쿼리 옵션 설정 시도
                      if ("limit" in query) {
                        (query as any).limit = 100;
                      }

                      // metaDataKeys가 있다면 설정 (Sendbird SDK 버전에 따라 다를 수 있음)
                      if ("metaDataKeys" in query) {
                        (query as any).metaDataKeys = [
                          "email",
                          "contact",
                          "profileName",
                        ];
                      }

                      let allUsers: any[] = [];
                      let user = null;

                      // 먼저 userId로 직접 사용자 조회 시도
                      if (userId && userId !== "user") {
                        try {
                          // 사용자 쿼리 생성 후 직접 조회
                          const userQuery =
                            stores.sdkStore.sdk.createApplicationUserListQuery();
                          (userQuery as any).userIdsFilter = [userId];
                          const users = await userQuery.next();
                          if (users.length > 0) {
                            user = users[0];
                          }
                        } catch (err) {}
                      }

                      // 직접 조회 실패시 쿼리로 찾기
                      if (!user) {
                        // 모든 페이지를 순회하며 사용자 찾기
                        while (query.hasNext && allUsers.length < 1000) {
                          // 안전장치로 1000명까지만
                          const userList = await query.next();
                          allUsers = allUsers.concat(userList);

                          // 현재 페이지에서 사용자 찾기
                          user = userList.find(
                            (u) =>
                              u.nickname === senderName || u.userId === userId,
                          );

                          if (user) {
                            break;
                          }
                        }
                      }

                      if (user) {
                        setSelectedUser({
                          userId: user.userId,
                          nickname: user.nickname || senderName || "User",
                          profileUrl: user.profileUrl || profileUrl,
                          connectionStatus:
                            user.connectionStatus === "online"
                              ? "online"
                              : "offline",
                          lastSeenAt: user.lastSeenAt || Date.now(),
                          createdAt: Date.now() - 30 * 24 * 60 * 60 * 1000,
                          metaData: user.metaData || {},
                        });
                      } else {
                        // userId로 직접 사용자 정보 가져오기 시도
                        try {
                          // 채널의 멤버 목록에서 찾기
                          if (currentChannel && currentChannel.members) {
                            const channelMember = currentChannel.members.find(
                              (m: Member) =>
                                m.nickname === senderName ||
                                m.userId === userId,
                            );
                            if (channelMember) {
                              setSelectedUser({
                                userId: channelMember.userId,
                                nickname:
                                  channelMember.nickname ||
                                  senderName ||
                                  "User",
                                profileUrl:
                                  channelMember.profileUrl || profileUrl,
                                connectionStatus:
                                  channelMember.connectionStatus === "online"
                                    ? "online"
                                    : "offline",
                                lastSeenAt:
                                  channelMember.lastSeenAt || Date.now(),
                                createdAt:
                                  Date.now() - 30 * 24 * 60 * 60 * 1000,
                                metaData: channelMember.metaData || {},
                              });
                              return;
                            }
                          }
                        } catch (err) {
                          console.error(
                            "Error fetching from channel members:",
                            err,
                          );
                        }

                        // Fallback to DOM extracted data
                        setSelectedUser({
                          userId: userId,
                          nickname: senderName || "User",
                          profileUrl: profileUrl,
                          connectionStatus: "offline",
                          lastSeenAt: Date.now(),
                          createdAt: Date.now() - 30 * 24 * 60 * 60 * 1000,
                          metaData: {},
                        });
                      }
                    } catch (error) {
                      console.error(
                        "Error fetching user from Sendbird:",
                        error,
                      );
                      // Failed to fetch user from Sendbird
                      // Fallback to DOM extracted data
                      setSelectedUser({
                        userId: userId,
                        nickname: senderName || "User",
                        profileUrl: profileUrl,
                        connectionStatus: "offline",
                        lastSeenAt: Date.now(),
                        createdAt: Date.now() - 30 * 24 * 60 * 60 * 1000,
                        metaData: {},
                      });
                    }
                  } else {
                    // Fallback if no SDK or userId
                    setSelectedUser({
                      userId: userId,
                      nickname: senderName || "User",
                      profileUrl: profileUrl,
                      connectionStatus: "offline",
                      lastSeenAt: Date.now(),
                      createdAt: Date.now() - 30 * 24 * 60 * 60 * 1000,
                      metaData: {},
                    });
                  }

                  setUserProfilePosition(position);
                  setUserProfileCardVisible(true);
                };

                fetchUserAndShow();
              }
            }
          }
        };

        document.addEventListener("click", handleProfileClick, true);

        return () => {
          document.removeEventListener("click", handleProfileClick, true);
        };
      }, 500);

      return () => clearTimeout(timer);
    }
  }, [showChannel, stores]);

  const handleSaveChannelInfo = async (updates: {
    channelName: string;
    channelImage?: string;
    channelImageFile?: File;
  }) => {
    if (currentChannel && stores?.sdkStore?.sdk) {
      try {
        // 채널 업데이트를 위한 파라미터 준비
        const params: any = {
          name: updates.channelName,
        };

        // 이미지 파일이 있으면 SendBird에 직접 업로드
        if (updates.channelImageFile) {
          params.coverImage = updates.channelImageFile;
        } else if (updates.channelImage !== undefined) {
          // 이미지 삭제 또는 URL 업데이트
          if (updates.channelImage === "") {
            params.coverUrl = "";
          } else {
            params.coverUrl = updates.channelImage;
          }
        }

        // 채널 정보 업데이트
        let updatedChannel;
        try {
          updatedChannel = await currentChannel.updateChannel(params);
        } catch (updateError) {
          // SendBird 업데이트 실패 시 폴백 URL 사용
          if (updates.channelImageFile && updates.channelImage) {
            const fallbackParams = {
              name: updates.channelName,
              coverUrl: updates.channelImage,
            };
            updatedChannel = await currentChannel.updateChannel(fallbackParams);
          } else {
            throw updateError;
          }
        }

        // 현재 채널 상태 업데이트
        setCurrentChannel(updatedChannel);

        // ChannelList 강제 리렌더링
        setChannelListKey((prev) => prev + 1);
      } catch (error) {
        console.error("Failed to update channel info:", error);
        throw error;
      }
    }
  };

  const handleSaveProfile = async (updates: {
    nickname?: string;
    profileUrl?: string;
  }) => {
    if (currentUser && stores?.sdkStore?.sdk) {
      try {
        const updatePayload: any = {
          nickname: updates.nickname,
        };

        // 프로필 이미지 처리 - 항상 값을 전달
        // Sendbird는 null을 받아들이지 않을 수 있으므로 기본 이미지 URL 사용
        const DEFAULT_PROFILE_URL =
          "https://static.sendbird.com/sample/profiles/profile_placeholder.png";
        updatePayload.profile_url = updates.profileImage || DEFAULT_PROFILE_URL;

        await updateSendbirdUser({
          userId: currentUser.userId,
          updates: updatePayload,
        });

        // SDK의 현재 사용자 정보도 업데이트
        const sdk = stores.sdkStore.sdk;
        if (sdk.currentUser) {
          // 프로필 이미지 삭제 시 기본 이미지 URL 사용
          const DEFAULT_PROFILE_URL =
            "https://static.sendbird.com/sample/profiles/profile_placeholder.png";
          const profileUpdate = {
            nickname: updates.nickname,
            profileUrl: updates.profileImage || DEFAULT_PROFILE_URL,
          };

          await sdk.updateCurrentUserInfo(profileUpdate);

          // SDK에서 최신 사용자 정보 다시 가져오기
          await sdk.connect(currentUser.userId);

          // 업데이트된 사용자 정보로 currentUser 상태 업데이트
          setCurrentUser({
            ...currentUser,
            nickname: updates.nickname,
            profileUrl: updates.profileImage || DEFAULT_PROFILE_URL,
          });
        }

        // 프로필 업데이트 후 모달 닫기
        setEditProfileModalVisible(false);

        // 현재 채널이 열려있다면 URL 저장
        if (currentChannel?.url) {
          setPendingChannelUrl(currentChannel.url);
        }

        // ChannelList 강제 리렌더링 (프로필 이미지 즉시 반영)
        setChannelListKey((prev) => prev + 1);
      } catch (error) {
        // Failed to update profile
      }
    }
  };

  const hiddenPaths = [
    "/login",
    "/signup",
    "/corporate-signup",
    "/staff-signup",
    "/temporary-signup",
    "/find-password",
    "/subscription-management",
    "/subscription-plan-signup",
    "/enterprise-signup",
    "/credit",
  ];

  const currentPath = window.location.pathname;
  const shouldHideChat = hiddenPaths.some((path) =>
    currentPath.toLowerCase().includes(path.toLowerCase()),
  );

  if (shouldHideChat) {
    return null;
  }

  return (
    <>
      <GlobalNoChannelBorder />

      <FloatingButton
        $showChannel={showChannel}
        onClick={() => {
          if (showChannel && currentChannel?.url) {
            // 채팅창 닫기 전에 현재 draft 저장
            // Draft saving is handled by saveDraftOnClose from useChatDraft hook
            saveDraftOnClose();
          }
          setShowChannel((prev) => !prev);
        }}
      >
        {showChannel ? (
          <Icon iconSrc={DeleteSvg} iconSize={20} />
        ) : (
          <>
            <Icon iconSrc={ChatSvg} iconSize={20} />
            {totalUnreadCount > 0 && (
              <UnreadBadge>{totalUnreadCount}</UnreadBadge>
            )}
          </>
        )}
      </FloatingButton>

      {/* Chat initialization status */}
      <ChatInitializationStatus
        isLoading={isLoading}
        error={error}
        retryCount={retryCount}
        maxRetries={maxRetries}
        showChannel={showChannel}
        onRetry={onRetry}
      />

      {showChannel && (
        <ChannelModal
          onClick={() => {
            // 모달 배경 클릭 시 draft 처리
            if (currentChannel?.url) {
              // Draft saving is handled by saveDraftOnClose from useChatDraft hook
              saveDraftOnClose();
            }
            setShowChannel(false);
          }}
        >
          <ChannelModalContent
            onClick={(e) => e.stopPropagation()}
            $onlyList={onlyList}
            $withSidePanel={withSidePanel}
          >
            <FlexRow>
              <ChannelListSection>
                <ChannelList
                  key={channelListKey}
                  onChannelSelect={(channel: GroupChannel) => {
                    // 이전 채널의 draft 처리
                    if (currentChannel?.url) {
                      // Draft saving is handled by saveDraftOnClose from useChatDraft hook
                      saveDraftOnClose();
                    }

                    setCurrentChannel(channel);

                    // 입력 필드 즉시 초기화 (이전 채널의 draft가 보이지 않도록)
                    setTimeout(() => {
                      const inputField = document.querySelector(
                        ".sendbird-message-input-text-field",
                      ) as HTMLDivElement;
                      if (inputField) {
                        inputField.textContent = "";
                        inputField.innerHTML = "";
                      }
                    }, 0);

                    // 채널 선택 시 읽음 처리
                    if (channel?.url) {
                    }
                  }}
                  disableAutoSelect={true}
                  allowProfileEdit={false}
                  onProfileEditSuccess={() => {}}
                  renderChannelPreview={(props) => {
                    const { channel, onClick, tabIndex, isActive } = props;

                    // 현재 선택된 채널인지 확인
                    const isSelected = currentChannel?.url === channel.url;

                    const currentUserId =
                      stores?.sdkStore?.sdk?.currentUser?.userId;

                    // SendBird 기본 프로필 URL 체크 함수
                    const isDefaultProfileUrl = (url: string) => {
                      return (
                        !url ||
                        url.includes("profile_placeholder.png") ||
                        url.includes("sample/cover")
                      );
                    };

                    // 채널에 커스텀 이미지가 설정되어 있는지 확인
                    const hasCustomImage =
                      channel.coverUrl &&
                      channel.coverUrl !== "" &&
                      !isDefaultProfileUrl(channel.coverUrl);

                    // 마지막 메시지를 보낸 사용자 확인
                    const lastMessage = channel.lastMessage;
                    const lastSenderId =
                      lastMessage?.sender?.userId ||
                      lastMessage?._sender?.userId;
                    const isLastMessageFromBot =
                      lastSenderId === "ecoya_export_bot";

                    // 현재 사용자와 봇을 제외한 다른 멤버들만 필터링
                    // custom_type이 'contract'인 경우 operators도 제외
                    const otherMembers = channel.members.filter(
                      (member: Member) => {
                        // 봇 제외 (ecoya_export_bot)
                        if (member.userId === "ecoya_export_bot") {
                          return false;
                        }

                        // 현재 사용자 제외
                        if (member.userId === currentUserId) {
                          return false;
                        }

                        // custom_type이 'contract'인 경우 operators 제외
                        if (
                          channel.customType === "contract" &&
                          channel.operators
                        ) {
                          const isOperator = channel.operators.some(
                            (operator: Member) =>
                              operator.userId === member.userId,
                          );
                          if (isOperator) {
                            return false;
                          }
                        }

                        return true;
                      },
                    );

                    // 기본 채널 프리뷰 렌더링
                    return (
                      <div
                        className={`sendbird-channel-preview ${isActive ? "sendbird-channel-preview--active" : ""}`}
                        onClick={onClick}
                        tabIndex={tabIndex}
                        role="button"
                        onKeyDown={(e) => {
                          if (e.key === "Enter" || e.key === " ") {
                            onClick();
                          }
                        }}
                        style={{
                          position: "relative",
                          backgroundColor: isSelected
                            ? "#D7E7F9"
                            : "transparent",
                          borderLeft: isSelected ? "4px solid #166DD7" : "none",
                          paddingLeft: isSelected ? "12px" : "16px",
                          transition: "background-color 0.2s",
                        }}
                        onMouseEnter={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.backgroundColor = "#F8F8F8";
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!isSelected) {
                            e.currentTarget.style.backgroundColor =
                              "transparent";
                          }
                        }}
                      >
                        <div className="sendbird-channel-preview__avatar">
                          {hasCustomImage &&
                          !isDefaultProfileUrl(channel.coverUrl) ? (
                            // 채널에 커스텀 이미지가 있고 sample/cover가 아닌 경우 표시
                            <div
                              className="sendbird-avatar"
                              style={{ width: 56, height: 56 }}
                            >
                              <img
                                className="sendbird-avatar-img"
                                src={channel.coverUrl}
                                alt={channel.name || "Channel"}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  borderRadius: "50%",
                                }}
                              />
                            </div>
                          ) : isLastMessageFromBot &&
                            otherMembers.length === 0 ? (
                            // 봇이 마지막 메시지를 보낸 경우 봇의 프로필 이미지 표시 (1:1 채팅에서만)
                            <div
                              className="sendbird-avatar"
                              style={{ width: 56, height: 56 }}
                            >
                              {(() => {
                                // 봇 멤버 찾기
                                const botMember = channel.members.find(
                                  (member: Member) =>
                                    member.userId === "ecoya_export_bot",
                                );
                                const botProfileUrl = botMember?.profileUrl;

                                // 봇의 profileUrl이 있으면 무조건 사용, 없으면 기본 아바타
                                return botProfileUrl ? (
                                  <img
                                    className="sendbird-avatar-img"
                                    src={botProfileUrl}
                                    alt="Bot"
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      borderRadius: "50%",
                                    }}
                                  />
                                ) : (
                                  // profileUrl이 없는 경우에만 기본 아바타
                                  <div
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      borderRadius: "50%",
                                      background: colorSet.gray7,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <img
                                      src={ChatIconUserSvg}
                                      alt="Default Bot"
                                      style={{
                                        width: "32px",
                                        height: "32px",
                                        filter: "brightness(0) invert(1)",
                                      }}
                                    />
                                  </div>
                                );
                              })()}
                            </div>
                          ) : otherMembers.length === 1 ? (
                            // 단일 사용자 아바타 (봇과 나를 제외한 상대방 1명)
                            <div
                              className="sendbird-avatar"
                              style={{ width: 56, height: 56 }}
                            >
                              {(() => {
                                const imageUrl = hasCustomImage
                                  ? channel.coverUrl
                                  : otherMembers[0].profileUrl;

                                return !isDefaultProfileUrl(imageUrl) ? (
                                  <img
                                    className="sendbird-avatar-img"
                                    src={imageUrl}
                                    alt={otherMembers[0]?.nickname || ""}
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      borderRadius: "50%",
                                    }}
                                  />
                                ) : (
                                  <div
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      borderRadius: "50%",
                                      background: colorSet.gray7,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <img
                                      src={ChatIconUserSvg}
                                      alt="Default User"
                                      style={{
                                        width: "32px",
                                        height: "32px",
                                        filter: "brightness(0) invert(1)",
                                      }}
                                    />
                                  </div>
                                );
                              })()}
                            </div>
                          ) : otherMembers.length === 2 ? (
                            // 2명 사용자 아바타 (봇과 나를 제외한 상대방 2명)
                            <div
                              className="sendbird-avatar"
                              style={{
                                width: 56,
                                height: 56,
                                position: "relative",
                              }}
                            >
                              <div
                                style={{
                                  position: "absolute",
                                  width: 38,
                                  height: 38,
                                  top: 0,
                                  left: 0,
                                }}
                              >
                                {!isDefaultProfileUrl(
                                  otherMembers[0].profileUrl,
                                ) ? (
                                  <img
                                    src={otherMembers[0].profileUrl}
                                    alt={otherMembers[0].nickname || ""}
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      borderRadius: "50%",
                                      border: "2px solid white",
                                    }}
                                  />
                                ) : (
                                  <div
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      borderRadius: "50%",
                                      background: colorSet.gray7,
                                      border: "2px solid white",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <img
                                      src={ChatIconUserSvg}
                                      alt="Default User"
                                      style={{
                                        width: "20px",
                                        height: "20px",
                                        filter: "brightness(0) invert(1)",
                                      }}
                                    />
                                  </div>
                                )}
                              </div>
                              <div
                                style={{
                                  position: "absolute",
                                  width: 38,
                                  height: 38,
                                  bottom: 0,
                                  right: 0,
                                }}
                              >
                                {!isDefaultProfileUrl(
                                  otherMembers[1].profileUrl,
                                ) ? (
                                  <img
                                    src={otherMembers[1].profileUrl}
                                    alt={otherMembers[1].nickname || ""}
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      borderRadius: "50%",
                                      border: "2px solid white",
                                    }}
                                  />
                                ) : (
                                  <div
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      borderRadius: "50%",
                                      background: colorSet.gray7,
                                      border: "2px solid white",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <img
                                      src={ChatIconUserSvg}
                                      alt="Default User"
                                      style={{
                                        width: "20px",
                                        height: "20px",
                                        filter: "brightness(0) invert(1)",
                                      }}
                                    />
                                  </div>
                                )}
                              </div>
                            </div>
                          ) : otherMembers.length > 2 ? (
                            // 3명 이상일 때 - 처음 2명의 이미지와 나머지 숫자 표시
                            <div
                              className="sendbird-avatar"
                              style={{
                                width: 56,
                                height: 56,
                                position: "relative",
                              }}
                            >
                              <div
                                style={{
                                  position: "absolute",
                                  width: 38,
                                  height: 38,
                                  top: 0,
                                  left: 0,
                                }}
                              >
                                {!isDefaultProfileUrl(
                                  otherMembers[0].profileUrl,
                                ) ? (
                                  <img
                                    src={otherMembers[0].profileUrl}
                                    alt={otherMembers[0].nickname || ""}
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      borderRadius: "50%",
                                      border: "2px solid white",
                                    }}
                                  />
                                ) : (
                                  <div
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      borderRadius: "50%",
                                      background: colorSet.gray7,
                                      border: "2px solid white",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <img
                                      src={ChatIconUserSvg}
                                      alt="Default User"
                                      style={{
                                        width: "20px",
                                        height: "20px",
                                        filter: "brightness(0) invert(1)",
                                      }}
                                    />
                                  </div>
                                )}
                              </div>
                              <div
                                style={{
                                  position: "absolute",
                                  width: 38,
                                  height: 38,
                                  bottom: 0,
                                  right: 0,
                                }}
                              >
                                <div
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    borderRadius: "50%",
                                    background: "#e1e4e7",
                                    border: "2px solid white",
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: 14,
                                    fontWeight: 600,
                                    color: "#5e5e5e",
                                  }}
                                >
                                  +{otherMembers.length - 1}
                                </div>
                              </div>
                            </div>
                          ) : (
                            // 기본 아바타 (상대방이 없을 때)
                            <div
                              className="sendbird-avatar"
                              style={{ width: 56, height: 56 }}
                            >
                              <div
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  borderRadius: "50%",
                                  background: "#AEB7BC",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                }}
                              >
                                <img
                                  src={ChatIconUserSvg}
                                  alt="Default User"
                                  style={{
                                    width: "32px",
                                    height: "32px",
                                    filter: "brightness(0) invert(1)",
                                  }}
                                />
                              </div>
                            </div>
                          )}
                        </div>

                        <div className="sendbird-channel-preview__content">
                          <div className="sendbird-channel-preview__content__upper">
                            <div className="sendbird-channel-preview__content__upper__header">
                              {isLastMessageFromBot &&
                                otherMembers.length === 0 && (
                                  <img
                                    src={ChatIconBroadcastSvg}
                                    alt="Bot message"
                                    style={{
                                      width: "16px",
                                      height: "16px",
                                      marginRight: "4px",
                                      verticalAlign: "middle",
                                    }}
                                  />
                                )}
                              <span
                                className="sendbird-channel-preview__content__upper__header__channel-name sendbird-label sendbird-label--subtitle-2 sendbird-label--color-onbackground-1"
                                style={{
                                  color: isSelected ? "#166DD7" : undefined,
                                }}
                              >
                                {channel.customType === "contract" &&
                                otherMembers.length > 0
                                  ? otherMembers
                                      .map(
                                        (m: Member) =>
                                          m.metaData?.profileName || m.nickname,
                                      )
                                      .filter((n: string) => n)
                                      .join(", ")
                                  : channel.name || "Channel"}
                              </span>
                            </div>
                            <span className="sendbird-channel-preview__content__upper__last-message-at sendbird-label sendbird-label--caption-3 sendbird-label--color-onbackground-2">
                              {channel.lastMessage
                                ? formatChannelListTime(
                                    channel.lastMessage.createdAt,
                                  )
                                : ""}
                            </span>
                          </div>
                          <div className="sendbird-channel-preview__content__lower">
                            <span
                              className="sendbird-channel-preview__content__lower__last-message sendbird-label sendbird-label--body-2 sendbird-label--color-onbackground-3"
                              style={{ flex: 1 }}
                            >
                              {channel.lastMessage?.message ||
                                "No messages yet"}
                            </span>
                            {channel.unreadMessageCount > 0 && (
                              <div
                                style={{
                                  backgroundColor: "#166DD7",
                                  borderRadius: "18px",
                                  minWidth: "22px",
                                  height: "22px",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  padding: "0 6px",
                                  marginLeft: "4px",
                                  flexShrink: 0,
                                }}
                              >
                                <span
                                  style={{
                                    color: "white",
                                    fontSize: "13px",
                                    fontWeight: 500,
                                    lineHeight: "14px",
                                    fontFamily: "Pretendard",
                                  }}
                                >
                                  {channel.unreadMessageCount > 99
                                    ? "99+"
                                    : channel.unreadMessageCount}
                                </span>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  }}
                />
              </ChannelListSection>
              {currentChannel && (
                <ChannelSection id="channel-section">
                  <div style={{ width: "100%", height: "100%" }}>
                    <ChannelWrapper
                      key={`channel-${currentChannel.url}`}
                      channelUrl={currentChannel.url}
                      onChatHeaderActionClick={() => {
                        if (showMessageSearch) {
                          setShowMessageSearch(false);
                          setShowSettings(true);
                        } else {
                          setShowSettings((prev) => !prev);
                        }
                      }}
                      showSearchIcon={true}
                      onSearchClick={() => {
                        if (showSettings) {
                          setShowSettings(false);
                          setShowMessageSearch(true);
                        } else {
                          setShowMessageSearch((prev) => !prev);
                        }
                      }}
                      onBeforeSendUserMessage={(text, quotedMessage) => {
                        // 메시지 전송 시 draft 삭제 - useChatDraft hook에서 제공
                        if (currentChannel) {
                          deleteDraftFromLocalStorage(currentChannel.url);
                        }

                        return {
                          message: text,
                          mentionedUserIds: [],
                          parentMessageId: quotedMessage?.messageId,
                        };
                      }}
                      onBeforeSendFileMessage={(file, quotedMessage) => {
                        // 파일 전송 시에도 draft 삭제 - useChatDraft hook에서 제공
                        if (currentChannel) {
                          deleteDraftFromLocalStorage(currentChannel.url);
                        }

                        // 기본 파라미터 반환
                        return {
                          file,
                          fileName: file.name,
                          fileSize: file.size,
                          mimeType: file.type,
                          parentMessageId: quotedMessage?.messageId,
                        };
                      }}
                      renderChannelHeader={() => {
                        const currentUserId =
                          stores?.sdkStore?.sdk?.currentUser?.userId;

                        // SendBird 기본 프로필 URL 체크 함수
                        const isDefaultProfileUrl = (url: string) => {
                          return (
                            !url ||
                            url.includes("profile_placeholder.png") ||
                            url.includes("sample/cover")
                          );
                        };

                        // 채널에 커스텀 이미지가 설정되어 있는지 확인
                        const hasCustomImage =
                          currentChannel.coverUrl &&
                          currentChannel.coverUrl !== "" &&
                          !isDefaultProfileUrl(currentChannel.coverUrl);

                        // 채널 정보 한 번만 로그 (useEffect로 이동 필요)

                        // 현재 사용자와 봇을 제외한 다른 멤버들만 필터링
                        // custom_type이 'contract'인 경우 operators도 제외
                        const otherMembers = currentChannel.members.filter(
                          (member: Member) => {
                            // 봇 제외 (ecoya_export_bot)
                            if (member.userId === "ecoya_export_bot")
                              return false;
                            // 현재 사용자 제외
                            if (member.userId === currentUserId) return false;

                            // custom_type이 'contract'인 경우 operators 제외
                            if (
                              currentChannel.customType === "contract" &&
                              currentChannel.operators
                            ) {
                              const isOperator = currentChannel.operators.some(
                                (operator: Member) =>
                                  operator.userId === member.userId,
                              );
                              if (isOperator) {
                                return false;
                              }
                            }

                            return true;
                          },
                        );

                        return (
                          <div
                            className="sendbird-chat-header"
                            style={{
                              padding: "16px",
                              borderBottom: "1px solid #E1F0FF",
                              display: "flex",
                              alignItems: "center",
                              backgroundColor: "#fff",
                            }}
                          >
                            <div
                              className="sendbird-chat-header--avatar--group-channel sendbird-avatar"
                              style={{
                                width: "32px",
                                height: "32px",
                                marginRight: "8px",
                              }}
                            >
                              {otherMembers.length === 1 ? (
                                // 단일 사용자 아바타 (봇과 나를 제외한 상대방 1명)
                                <>
                                  {(() => {
                                    const imageUrl =
                                      hasCustomImage &&
                                      !isDefaultProfileUrl(
                                        currentChannel.coverUrl,
                                      )
                                        ? currentChannel.coverUrl
                                        : otherMembers[0].profileUrl;
                                    return !isDefaultProfileUrl(imageUrl) ? (
                                      <img
                                        className="sendbird-avatar-img"
                                        src={imageUrl}
                                        alt={otherMembers[0].nickname || ""}
                                        style={{
                                          width: "100%",
                                          height: "100%",
                                          borderRadius: "50%",
                                          objectFit: "cover",
                                        }}
                                      />
                                    ) : (
                                      <div
                                        style={{
                                          width: "100%",
                                          height: "100%",
                                          borderRadius: "50%",
                                          backgroundColor: colorSet.gray7,
                                          display: "flex",
                                          alignItems: "center",
                                          justifyContent: "center",
                                        }}
                                      >
                                        <img
                                          src={ChatIconUserSvg}
                                          alt={otherMembers[0].nickname || ""}
                                          style={{
                                            width: "18px",
                                            height: "18px",
                                            filter: "brightness(0) invert(1)",
                                          }}
                                        />
                                      </div>
                                    );
                                  })()}
                                </>
                              ) : otherMembers.length > 1 ? (
                                // 그룹 채널 (2명 이상)
                                <div
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    borderRadius: "50%",
                                    background: colorSet.gray7,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                    fontSize: "12px",
                                    fontWeight: 600,
                                    color: "white",
                                  }}
                                >
                                  {otherMembers.length}
                                </div>
                              ) : (
                                // 기본 아바타
                                <div
                                  style={{
                                    width: "100%",
                                    height: "100%",
                                    borderRadius: "50%",
                                    backgroundColor: colorSet.gray7,
                                    display: "flex",
                                    alignItems: "center",
                                    justifyContent: "center",
                                  }}
                                >
                                  <img
                                    src={ChatIconUserSvg}
                                    alt="Default"
                                    style={{
                                      width: "18px",
                                      height: "18px",
                                      filter: "brightness(0) invert(1)",
                                    }}
                                  />
                                </div>
                              )}
                            </div>
                            <div
                              className="sendbird-ui-header__middle"
                              style={{ flex: 1 }}
                            >
                              <h2
                                className="sendbird-ui-header__middle__title sendbird-label sendbird-label--h-2 sendbird-label--color-onbackground-1"
                                style={{ margin: 0 }}
                              >
                                {currentChannel.customType === "contract" &&
                                otherMembers.length > 0
                                  ? otherMembers
                                      .map(
                                        (m: Member) =>
                                          m.metaData?.profileName || m.nickname,
                                      )
                                      .filter((n: string) => n)
                                      .join(", ")
                                  : currentChannel.name || "Channel"}
                              </h2>
                            </div>
                            <div
                              className="sendbird-ui-header__right"
                              style={{ display: "flex", gap: "16px" }}
                            >
                              {true && (
                                <button
                                  className="sendbird-iconbutton"
                                  onClick={() => {
                                    if (showSettings) {
                                      setShowSettings(false);
                                      setShowMessageSearch(true);
                                    } else {
                                      setShowMessageSearch((prev) => !prev);
                                    }
                                  }}
                                  style={{
                                    background: "none",
                                    border: "none",
                                    cursor: "pointer",
                                    padding: "5px",
                                    borderRadius: "4px",
                                    transition: "background-color 0.2s",
                                  }}
                                  onMouseEnter={(e) => {
                                    e.currentTarget.style.backgroundColor =
                                      "#F1F4F6";
                                  }}
                                  onMouseLeave={(e) => {
                                    e.currentTarget.style.backgroundColor =
                                      "transparent";
                                  }}
                                  onMouseDown={(e) => {
                                    e.currentTarget.style.backgroundColor =
                                      "#E7EDF0";
                                  }}
                                  onMouseUp={(e) => {
                                    e.currentTarget.style.backgroundColor =
                                      "#F1F4F6";
                                  }}
                                >
                                  <svg
                                    width="24"
                                    height="24"
                                    viewBox="0 0 64 64"
                                    fill="none"
                                    xmlns="http://www.w3.org/2000/svg"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      clipRule="evenodd"
                                      d="M26.6667 48.0007C14.8846 48.0007 5.33337 38.4494 5.33337 26.6673C5.33337 14.8852 14.8846 5.33398 26.6667 5.33398C38.4488 5.33398 48 14.8852 48 26.6673C48 31.5972 46.3278 36.1365 43.5197 39.749L57.1897 53.4191C58.2311 54.4605 58.2311 56.1489 57.1897 57.1903C56.1483 58.2317 54.4599 58.2317 53.4185 57.1903L39.7484 43.5203C36.1359 46.3284 31.5966 48.0007 26.6667 48.0007ZM26.6667 42.6673C35.5033 42.6673 42.6667 35.5039 42.6667 26.6673C42.6667 17.8308 35.5033 10.6673 26.6667 10.6673C17.8301 10.6673 10.6667 17.8308 10.6667 26.6673C10.6667 35.5039 17.8301 42.6673 26.6667 42.6673Z"
                                      fill="#166DD7"
                                    />
                                  </svg>
                                </button>
                              )}
                              <button
                                className="sendbird-iconbutton"
                                onClick={() => {
                                  if (showMessageSearch) {
                                    setShowMessageSearch(false);
                                    setShowSettings(true);
                                  } else {
                                    setShowSettings((prev) => !prev);
                                  }
                                }}
                                style={{
                                  background: "none",
                                  border: "none",
                                  cursor: "pointer",
                                  padding: "5px",
                                  borderRadius: "4px",
                                  transition: "background-color 0.2s",
                                }}
                                onMouseEnter={(e) => {
                                  e.currentTarget.style.backgroundColor =
                                    "#F1F4F6";
                                }}
                                onMouseLeave={(e) => {
                                  e.currentTarget.style.backgroundColor =
                                    "transparent";
                                }}
                                onMouseDown={(e) => {
                                  e.currentTarget.style.backgroundColor =
                                    "#E7EDF0";
                                }}
                                onMouseUp={(e) => {
                                  e.currentTarget.style.backgroundColor =
                                    "#F1F4F6";
                                }}
                              >
                                <svg
                                  width="24"
                                  height="24"
                                  viewBox="0 0 64 64"
                                  fill="none"
                                  xmlns="http://www.w3.org/2000/svg"
                                >
                                  <path
                                    fillRule="evenodd"
                                    clipRule="evenodd"
                                    d="M32 2.66602C48.2003 2.66602 61.3333 15.799 61.3333 31.9993C61.3333 48.1997 48.2003 61.3327 32 61.3327C15.7996 61.3327 2.66666 48.1997 2.66666 31.9993C2.66666 15.799 15.7996 2.66602 32 2.66602ZM32 7.99935C18.7452 7.99935 7.99999 18.7445 7.99999 31.9993C7.99999 45.2542 18.7452 55.9993 32 55.9993C45.2548 55.9993 56 45.2542 56 31.9993C56 18.7445 45.2548 7.99935 32 7.99935ZM33.6667 29.3327C34.2189 29.3327 34.6667 29.7804 34.6667 30.3327V44.3327C34.6667 44.885 34.2189 45.3327 33.6667 45.3327H30.3333C29.781 45.3327 29.3333 44.885 29.3333 44.3327V30.3327C29.3333 29.7804 29.781 29.3327 30.3333 29.3327H33.6667ZM32 21.3327C33.4727 21.3327 34.6667 22.5266 34.6667 23.9993C34.6667 25.4721 33.4727 26.666 32 26.666C30.5272 26.666 29.3333 25.4721 29.3333 23.9993C29.3333 22.5266 30.5272 21.3327 32 21.3327Z"
                                    fill="#166DD7"
                                  />
                                </svg>
                              </button>
                            </div>
                          </div>
                        );
                      }}
                    />
                  </div>
                </ChannelSection>
              )}
              {currentChannel && (showMessageSearch || showSettings) && (
                <SidePanelSection>
                  {showMessageSearch ? (
                    <MessageSearchWithScroll
                      key={`search-${localStorage.getItem("i18nextLng") || i18n.language}`}
                      channelUrl={currentChannel.url}
                      onClose={() => setShowMessageSearch(false)}
                      onUserProfileClick={(user, position) => {
                        setSelectedUser(user);
                        setUserProfilePosition(position);
                        setUserProfileCardVisible(true);
                      }}
                      authUser={authUser}
                      setCurrentUser={setCurrentUser}
                      setEditProfileModalVisible={setEditProfileModalVisible}
                    />
                  ) : (
                    <ChannelSettings
                      channelUrl={currentChannel.url}
                      onCloseClick={() => setShowSettings(false)}
                      renderChannelProfile={() => {
                        const currentUserId =
                          stores?.sdkStore?.sdk?.currentUser?.userId;

                        // SendBird 기본 프로필 URL 체크 함수
                        const isDefaultProfileUrl = (url: string) => {
                          return (
                            !url ||
                            url.includes("profile_placeholder.png") ||
                            url.includes("sample/cover")
                          );
                        };

                        // 채널에 커스텀 이미지가 설정되어 있는지 확인
                        const hasCustomImage =
                          currentChannel.coverUrl &&
                          currentChannel.coverUrl !== "";

                        // 현재 사용자와 봇을 제외한 다른 멤버들만 필터링
                        // custom_type이 'contract'인 경우 operators도 제외
                        const otherMembers = currentChannel.members.filter(
                          (member: Member) => {
                            // 봇 제외 (ecoya_export_bot)
                            if (member.userId === "ecoya_export_bot")
                              return false;
                            // 현재 사용자 제외
                            if (member.userId === currentUserId) return false;

                            // custom_type이 'contract'인 경우 operators 제외
                            if (
                              currentChannel.customType === "contract" &&
                              currentChannel.operators
                            ) {
                              const isOperator = currentChannel.operators.some(
                                (operator: Member) =>
                                  operator.userId === member.userId,
                              );
                              if (isOperator) {
                                return false;
                              }
                            }

                            return true;
                          },
                        );

                        return (
                          <div
                            className="sendbird-channel-profile"
                            style={{
                              padding: "0",
                              borderBottom: "1px solid #E7EDF0",
                              height: "160px",
                              position: "relative",
                            }}
                          >
                            {/* Edit 버튼 - 피그마 디자인에 맞게 수정 */}
                            <button
                              onClick={() => setEditChannelModalVisible(true)}
                              style={{
                                position: "absolute",
                                top: "8px",
                                right: "16px",
                                background: "transparent",
                                border: "none",
                                cursor: "pointer",
                                padding: "4px 0",
                                borderRadius: "4px",
                                transition: "color 0.2s",
                                color: "#166DD7",
                                fontFamily: "Pretendard, sans-serif",
                                fontWeight: 600,
                                fontSize: "13px",
                                lineHeight: "18px",
                                zIndex: 10,
                              }}
                              onMouseEnter={(e) => {
                                e.currentTarget.style.color = "#1150AA";
                              }}
                              onMouseLeave={(e) => {
                                e.currentTarget.style.color = "#166DD7";
                              }}
                            >
                              Edit
                            </button>

                            {/* 아바타 영역 - 피그마 디자인에 맞게 수정 */}
                            <div
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                                paddingTop: "24px",
                                paddingBottom: "16px",
                                height: "100%",
                                justifyContent: "center",
                              }}
                            >
                              <div
                                className="sendbird-chat-header--avatar--group-channel sendbird-avatar"
                                style={{
                                  width: "80px",
                                  height: "80px",
                                  marginBottom: "16px",
                                  borderRadius: "50%",
                                  overflow: "hidden",
                                  display: "flex",
                                  alignItems: "center",
                                  justifyContent: "center",
                                  flexShrink: 0,
                                  position: "relative",
                                }}
                              >
                                {otherMembers.length === 1 ? (
                                  // 단일 사용자 아바타 (봇과 나를 제외한 상대방 1명)
                                  <>
                                    {(() => {
                                      // 채널 리스트와 동일한 우선순위: 채널 커스텀 이미지 → 상대방 프로필 이미지
                                      const imageUrl =
                                        hasCustomImage &&
                                        !isDefaultProfileUrl(
                                          currentChannel.coverUrl,
                                        )
                                          ? currentChannel.coverUrl
                                          : otherMembers[0].profileUrl;
                                      return imageUrl &&
                                        !isDefaultProfileUrl(imageUrl) ? (
                                        <img
                                          className="sendbird-avatar-img"
                                          src={imageUrl}
                                          alt={otherMembers[0].nickname || ""}
                                          style={{
                                            width: "100%",
                                            height: "100%",
                                            borderRadius: "50%",
                                            objectFit: "cover",
                                          }}
                                        />
                                      ) : (
                                        <div
                                          style={{
                                            width: "100%",
                                            height: "100%",
                                            borderRadius: "50%",
                                            backgroundColor: "#AEB7BC",
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                          }}
                                        >
                                          <img
                                            src={ChatIconUserSvg}
                                            alt={otherMembers[0].nickname || ""}
                                            style={{
                                              width: "45.714px",
                                              height: "45.714px",
                                              filter: "brightness(0) invert(1)",
                                            }}
                                          />
                                        </div>
                                      );
                                    })()}
                                  </>
                                ) : otherMembers.length > 1 ? (
                                  // 그룹 채널 (2명 이상)
                                  <div
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      borderRadius: "50%",
                                      background: "#AEB7BC",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      fontSize: "24px",
                                      fontWeight: 600,
                                      color: "white",
                                    }}
                                  >
                                    {otherMembers.length}
                                  </div>
                                ) : (
                                  // 기본 아바타
                                  <div
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      borderRadius: "50%",
                                      backgroundColor: "#AEB7BC",
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <img
                                      src={ChatIconUserSvg}
                                      alt="Default"
                                      style={{
                                        width: "45.714px",
                                        height: "45.714px",
                                        filter: "brightness(0) invert(1)",
                                      }}
                                    />
                                  </div>
                                )}
                              </div>

                              {/* 채널 이름 - 피그마 디자인에 맞게 수정 */}
                              <h3
                                style={{
                                  margin: "0",
                                  fontFamily: "Roboto, sans-serif",
                                  fontWeight: 700,
                                  fontSize: "18px",
                                  lineHeight: "24px",
                                  color: "#222729",
                                  textAlign: "center",
                                  maxWidth: "100%",
                                  padding: "0 16px",
                                  wordBreak: "break-word",
                                  overflowWrap: "break-word",
                                }}
                              >
                                {currentChannel.customType === "contract" &&
                                otherMembers.length > 0
                                  ? otherMembers
                                      .map(
                                        (m: Member) =>
                                          m.metaData?.profileName || m.nickname,
                                      )
                                      .filter((n: string) => n)
                                      .join(", ")
                                  : currentChannel.name || "Channel"}
                              </h3>
                            </div>
                          </div>
                        );
                      }}
                      renderUserProfile={() => {
                        const currentUserId =
                          stores?.sdkStore?.sdk?.currentUser?.userId;

                        // SendBird 기본 프로필 URL 체크 함수
                        const isDefaultProfileUrl = (url: string) => {
                          return (
                            !url ||
                            url.includes("profile_placeholder.png") ||
                            url.includes("sample/cover")
                          );
                        };

                        // 채널에 커스텀 이미지가 설정되어 있는지 확인
                        const hasCustomImage =
                          currentChannel.coverUrl &&
                          currentChannel.coverUrl !== "";

                        // 현재 사용자와 봇을 제외한 다른 멤버들만 필터링
                        // custom_type이 'contract'인 경우 operators도 제외
                        const otherMembers = currentChannel.members.filter(
                          (member: Member) => {
                            // 봇 제외 (ecoya_export_bot)
                            if (member.userId === "ecoya_export_bot")
                              return false;
                            // 현재 사용자 제외
                            if (member.userId === currentUserId) return false;

                            // custom_type이 'contract'인 경우 operators 제외
                            if (
                              currentChannel.customType === "contract" &&
                              currentChannel.operators
                            ) {
                              const isOperator = currentChannel.operators.some(
                                (operator: Member) =>
                                  operator.userId === member.userId,
                              );
                              if (isOperator) {
                                return false;
                              }
                            }

                            return true;
                          },
                        );

                        return (
                          <div
                            className="sendbird-channel-profile"
                            style={{
                              padding: "24px",
                              borderBottom: "1px solid #eee",
                            }}
                          >
                            <div
                              className="sendbird-channel-profile__header"
                              style={{
                                display: "flex",
                                flexDirection: "column",
                                alignItems: "center",
                              }}
                            >
                              <div
                                className="sendbird-chat-header--avatar--group-channel sendbird-avatar"
                                style={{
                                  width: "80px",
                                  height: "80px",
                                  marginBottom: "16px",
                                }}
                              >
                                {otherMembers.length === 1 ? (
                                  // 단일 사용자 아바타 (봇과 나를 제외한 상대방 1명)
                                  <>
                                    {(() => {
                                      // 채널 리스트와 동일한 우선순위: 채널 커스텀 이미지 → 상대방 프로필 이미지
                                      const imageUrl =
                                        hasCustomImage &&
                                        !isDefaultProfileUrl(
                                          currentChannel.coverUrl,
                                        )
                                          ? currentChannel.coverUrl
                                          : otherMembers[0].profileUrl;
                                      return imageUrl &&
                                        !isDefaultProfileUrl(imageUrl) ? (
                                        <img
                                          className="sendbird-avatar-img"
                                          src={imageUrl}
                                          alt={otherMembers[0].nickname || ""}
                                          style={{
                                            width: "100%",
                                            height: "100%",
                                            borderRadius: "50%",
                                            objectFit: "cover",
                                          }}
                                        />
                                      ) : (
                                        <div
                                          style={{
                                            width: "100%",
                                            height: "100%",
                                            borderRadius: "50%",
                                            backgroundColor: colorSet.gray7,
                                            display: "flex",
                                            alignItems: "center",
                                            justifyContent: "center",
                                          }}
                                        >
                                          <img
                                            src={ChatIconUserSvg}
                                            alt={otherMembers[0].nickname || ""}
                                            style={{
                                              width: "45.714px",
                                              height: "45.714px",
                                              filter: "brightness(0) invert(1)",
                                            }}
                                          />
                                        </div>
                                      );
                                    })()}
                                  </>
                                ) : otherMembers.length > 1 ? (
                                  // 그룹 채널 (2명 이상)
                                  <div
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      borderRadius: "50%",
                                      background: colorSet.gray7,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                      fontSize: "24px",
                                      fontWeight: 600,
                                      color: "white",
                                    }}
                                  >
                                    {otherMembers.length}
                                  </div>
                                ) : (
                                  // 기본 아바타
                                  <div
                                    style={{
                                      width: "100%",
                                      height: "100%",
                                      borderRadius: "50%",
                                      backgroundColor: colorSet.gray7,
                                      display: "flex",
                                      alignItems: "center",
                                      justifyContent: "center",
                                    }}
                                  >
                                    <img
                                      src={ChatIconUserSvg}
                                      alt="Default"
                                      style={{
                                        width: "45.714px",
                                        height: "45.714px",
                                        filter: "brightness(0) invert(1)",
                                      }}
                                    />
                                  </div>
                                )}
                              </div>
                              <h3
                                className="sendbird-channel-profile__title sendbird-label sendbird-label--subtitle-2 sendbird-label--color-onbackground-1"
                                style={{ margin: "0 0 8px 0" }}
                              >
                                {currentChannel.customType === "contract" &&
                                otherMembers.length > 0
                                  ? otherMembers
                                      .map(
                                        (m: Member) =>
                                          m.metaData?.profileName || m.nickname,
                                      )
                                      .filter((n: string) => n)
                                      .join(", ")
                                  : currentChannel.name || "Channel"}
                              </h3>
                            </div>
                          </div>
                        );
                      }}
                    />
                  )}
                </SidePanelSection>
              )}
            </FlexRow>
          </ChannelModalContent>
        </ChannelModal>
      )}
      <EditProfileModal
        visible={editProfileModalVisible}
        onClose={() => {
          setEditProfileModalVisible(false);
        }}
        user={currentUser}
        onSave={handleSaveProfile}
      />
      <EditChannelInformationModal
        visible={editChannelModalVisible}
        onClose={() => {
          setEditChannelModalVisible(false);
        }}
        channel={currentChannel}
        onSave={handleSaveChannelInfo}
      />
      <UserProfileCard
        visible={userProfileCardVisible}
        onClose={() => {
          setUserProfileCardVisible(false);
          setSelectedUser(null);
          setUserProfilePosition(undefined);
        }}
        user={selectedUser}
        position={userProfilePosition}
      />
    </>
  );
};

export default FloatingChat;

const GlobalNoChannelBorder = createGlobalStyle`
  .sendbird-channel-preview--active {
    background-color: #D7E7F9 !important;
    position: relative !important;
  }
  .sendbird-channel-preview--active::before {
    content: '';
    position: absolute;
    left: 0;
    top: 0;
    bottom: 0;
    width: 4px;
    background-color: #166DD7;
  }
  .sendbird-channel-preview--active:hover {
    background-color: #D7E7F9 !important;
  }
  
  





  
  .sendbird-channel-profile .sendbird-channel-profile__avatar {
    margin-bottom: 8px !important;
  }

  /* 봇 메시지 버블 스타일 - 다양한 선택자 시도 */
  /* 방법 1: 메시지 컨테이너에 bot-message-container 클래스가 추가된 경우 */
  .bot-message-container .sendbird-text-message-item-body {
    background-color: #1C3D61 !important;
    color: #FFFFFF !important;
    transition: background-color 0.2s !important;
  }
  
  .bot-message-container .sendbird-text-message-item-body:hover {
    background-color: #0B3E91 !important;
  }
  
  /* 방법 2: 받은 메시지 중에서 봇 메시지 스타일 */
  .sendbird-user-message--incoming .bot-message .sendbird-text-message-item-body {
    background-color: #1C3D61 !important;
    color: #FFFFFF !important;
  }
  
  /* 방법 3: data 속성 활용 */
  [data-sb-sender-id="ecoya_export_bot"] .sendbird-text-message-item-body {
    background-color: #1C3D61 !important;
    color: #FFFFFF !important;
  }
  
  /* 방법 4: 봇 메시지임을 표시하는 클래스가 있는 경우 */
  .bot-message-style-applied .sendbird-text-message-item-body {
    background-color: #1C3D61 !important;
    color: #FFFFFF !important;
  }
  
  .bot-message .sendbird-message-content__middle__message-item-body {
    background-color: #1C3D61 !important;
  }
  
  .bot-message .sendbird-message-content__middle__message-item-body:hover {
    background-color: #0B3E91 !important;
  }
  
  /* 봇 메시지 텍스트 색상 */
  .bot-message .sendbird-text-message-item-body,
  .bot-message .sendbird-label--body-1 {
    color: #FFFFFF !important;
  }

  /* Sendbird 메시지 검색 아이템 display 속성 덮어쓰기 */
  .sendbird-message-search-item {
    display: block !important;
  }

  /* Sendbird 채널 헤더 아바타 크기 조정 */
  .sendbird-channel-header__title__left .sendbird-avatar {
    width: 31px !important;
    height: 31px !important;
  }
  
  /* 채널 헤더 우측 섹션 padding 조정 */
  .sendbird-channel-header .sendbird-channel-header__title .sendbird-channel-header__title__right {
    padding-top: 14px !important;
  }
  

  /* Sendbird 메시지 아바타 크기 조정 */
  .sendbird-message-content__left.use-reactions.incoming .sendbird-context-menu .sendbird-message-content__left__avatar.sendbird-avatar {
    width: 27px !important;
    height: 27px !important;
  }


  
  .sendbird-channel-profile__title {
    margin-bottom: 24px !important;
    padding-bottom: 0 !important;
  }
  .sendbird-channel-profile {
    padding-bottom: 24px !important;
  }
  .sendbird-channel-settings .sendbird-channel-settings__panel-item:first-child {
    margin-top: 0 !important;
  }
  
  .sendbird-channel-settings .sendbird-channel-profile + .sendbird-channel-settings__panel-item {
    margin-top: 0 !important;
  }
  .sendbird-user-message--outgoing .sendbird-user-message__body {
    background-color: #166DD7 !important;
  }
  .sendbird-user-message--outgoing:hover .sendbird-user-message__body {
    background-color: #1761C2 !important;
  }
  .sendbird-user-message--incoming:hover .sendbird-user-message__body {
    background-color: #E7EDF0 !important;
  }
  .sendbird-channel,
  .sendbird-channel__content,
  .sendbird-app__conversation,
  .sendbird-app__wrap,
  .sendbird-conversation,
  .sendbird-conversation__messages,
  .sendbird-conversation__footer,
  .sendbird-conversation__header,
  .sendbird-conversation__panel,
  .sendbird-conversation__scroll-container {
    border: none !important;
    box-shadow: none !important;
    background: #fff !important;
  }
  .sendbird-channel__content {
    border-top: none !important;
    border-right: none !important;
  }
  .sendbird-channel-settings-accordion__footer{
    display: none !important;
  }
  .sendbird-modal-root {
    z-index: ${zIndex.floatingChatSendbird} !important;
  }
  
  .sendbird-modal {
    z-index: ${zIndex.floatingChatSendbird} !important;
  }
  
  .sendbird-modal__backdrop {
    z-index: ${zIndex.floatingChatSendbird} !important;
  }
  
  .sendbird-modal__content {
    z-index: ${zIndex.floatingChatSendbird + 1} !important;
  }
  .sendbird-dropdown__reaction-bar{
    z-index: ${zIndex.floatingChatSendbird + 1} !important;
  }
  .sendbird-channel-settings__edit-modal,
  .sendbird-edit-channel__modal {
    z-index: ${zIndex.floatingChatSendbird + 1} !important;
  }
  body > .sendbird-portal,
  body > [class*="sendbird-modal"] {
    z-index: ${zIndex.floatingChatSendbird} !important;
  }
  .sendbird-user-profile,
  .sendbird-user-profile-modal,
  .sendbird-modal-root:has(.sendbird-user-profile) {
    display: none !important;
  }
  .sendbird-channel-preview__context-menu {
    display: none !important;
  }
  .sendbird-channel-list .sendbird-ui-header__right.sendbird-ui-header--is-desktop {
    display: none !important;
  }
  .sendbird-channel-preview:hover .sendbird-channel-preview__content__upper__last-message-at {
    display: inline !important;
    opacity: 1 !important;
    visibility: visible !important;
  }
  .sendbird-channel-header__title__right__user-id.sendbird-label.sendbird-label--body-2.sendbird-label--color-onbackground-2 {
    font-size: 0 !important;
    display: flex !important;
    align-items: center !important;
  }
  
  /* .sendbird-channel-header__title__right__user-id.sendbird-label.sendbird-label--body-2.sendbird-label--color-onbackground-2::before {
    content: '';
    display: inline-block;
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background-color: #259C72;
    margin-right: 4px;
  } */
  
  /* .sendbird-channel-header__title__right__user-id.sendbird-label.sendbird-label--body-2.sendbird-label--color-onbackground-2::after {
    content: 'Online';
    font-size: 14px;
    color: ${colorSet.gray6};
    font-family: "Pretendard", sans-serif;
    font-weight: 400;
  } */

  .sendbird-channel-preview__action button {
    opacity: 0 !important;
    pointer-events: none !important;
  }
  .sendbird-message-content__left .sendbird-avatar {
    cursor: pointer !important;
  }
  
  .sendbird-message-content__left .sendbird-avatar-img {
    cursor: pointer !important;
  }
  
  .sendbird-channel-preview__action .sendbird-iconbutton {
    display: none !important;
  }
  .sendbird-channel-header,
  .sendbird-channel-list > div:first-child {
    cursor: pointer !important;
    transition: background-color 0.2s ease;
  }
  
  .sendbird-channel-header:hover,
  .sendbird-channel-list > div:first-child:hover {
    background-color: rgba(0, 0, 0, 0.02);
  }
    .sendbird-channel-list > div:first-child img,
  .sendbird-channel-list > div:first-child [class*="avatar"] {
    cursor: pointer !important;
  }
  .sendbird-channel-settings__panel-item__leave-channel {
  display: none !important;
  }
  .channel-profile-form__avatar-button,
  .channel-profile-form__avatar-button.sendbird-textbutton--not-underline,
  .channel-profile-form__avatar-button.sendbird-color--onbackground-1 {
    border: 1px solid ${colorSet.gray7} !important;
    border-radius: 4px !important;
    padding: 6px 16px !important;
    color: ${colorSet.gray2} !important;
    background: white !important;
    font-weight: 600 !important;
    font-size: 13px !important;
    text-decoration: none !important;
    transition: color 0.2s, border-color 0.2s, background-color 0.2s !important;
  }
    .channel-profile-form__avatar-button:hover {
    color: #166DD7 !important;
    border-color: #166DD7 !important;
  }
  
  .channel-profile-form__avatar-button:hover .sendbird-label,
  .channel-profile-form__avatar-button:hover .sendbird-label--button-1,
  .channel-profile-form__avatar-button:hover .sendbird-label--color-primary {
    color: #166DD7 !important;
  }
    .channel-profile-form__avatar-button:active {
    color: #166DD7 !important;
    border-color: #166DD7 !important;
    background-color: #D7E7F9 !important;
  }
  
  .channel-profile-form__avatar-button:active .sendbird-label,
  .channel-profile-form__avatar-button:active .sendbird-label--button-1,
  .channel-profile-form__avatar-button:active .sendbird-label--color-primary {
    color: #166DD7 !important;
  }
    .channel-profile-form__avatar-button .sendbird-label,
  .channel-profile-form__avatar-button .sendbird-label--button-1,
  .channel-profile-form__avatar-button .sendbird-label--color-primary {
    color: ${colorSet.gray2} !important;
    font-family: "Pretendard", sans-serif !important;
    font-weight: 600 !important;
    font-size: 13px !important;
    line-height: 18px !important;
    letter-spacing: 0px !important;
  }
    .sendbird-modal__footer .sendbird-button--secondary {
    border: 1px solid ${colorSet.gray7} !important;
    background: white !important;
    color: ${colorSet.gray2} !important;
    transition: color 0.2s, border-color 0.2s, background-color 0.2s !important;
    outline: none !important;
  }
  
  .sendbird-modal__footer .sendbird-button--secondary:hover {
    color: #166DD7 !important;
    border-color: #166DD7 !important;
  }
  
  .sendbird-modal__footer .sendbird-button--secondary:hover span {
    color: #166DD7 !important;
  }
  
  .sendbird-modal__footer .sendbird-button--secondary:active {
    color: #166DD7 !important;
    border-color: #166DD7 !important;
    background-color: #D7E7F9 !important;
  }
  
  .sendbird-modal__footer .sendbird-button--secondary:focus {
    outline: none !important;
    box-shadow: none !important;
  }
  
  .sendbird-modal__footer .sendbird-button--primary {
    background: #166DD7 !important;
    border: none !important;
    color: white !important;
    transition: background-color 0.2s !important;
    outline: none !important;
  }
  
  .sendbird-modal__footer .sendbird-button--primary:hover {
    background: #1761C2 !important;
  }
  
  .sendbird-modal__footer .sendbird-button--primary:active {
    background: #1150AA !important;
  }
  
  .sendbird-modal__footer .sendbird-button--primary:focus {
    outline: none !important;
    box-shadow: none !important;
  }
  .sendbird-modal__footer .sendbird-button--primary:disabled {
    background: ${colorSet.gray4} !important;
    cursor: not-allowed !important;
  }
  @keyframes messageBounce {
    0%, 100% {
      transform: translateY(0);
    }
    25% {
      transform: translateY(-8px);
    }
    50% {
      transform: translateY(0);
    }
    75% {
      transform: translateY(-8px);
    }
  }
  .sendbird-message-highlight .sendbird-message-content__middle__message-item-body {
    animation: messageBounce 0.8s ease-in-out;
  }
  .sendbird-text-message-item-body {
    position: relative;
    transition: transform 0.2s ease;
  }
  
  /* 읽음 상태의 체크 색상을 전송 상태와 동일하게 변경 */
  .sendbird-icon-color--read {
    color: rgba(0, 0, 0, 0.38) !important;
  }
  
  .sendbird-message-status--sent.sendbird-icon-done-all {
    color: rgba(0, 0, 0, 0.38) !important;
  }
  
  .sendbird-icon-done-all.sendbird-icon-color--read svg path {
    fill: rgba(0, 0, 0, 0.38) !important;
  }
  
  .sendbird-message-status__icon.sendbird-icon-color--read svg {
    color: rgba(0, 0, 0, 0.38) !important;
  }
  
  /* Channel frozen notification 스타일 */
  .sendbird-notification--frozen {
    background-color: #AEB7BC !important;
  }
  
  .sendbird-notification--frozen .sendbird-notification__text {
    color: white !important;
  }
`;

const FloatingButton = styled.span<{ $showChannel: boolean }>`
  display: flex;
  position: fixed;
  border-radius: 50%;
  background-color: ${(props) =>
    props.$showChannel ? colorSet.gray7 : colorSet.blue2};
  cursor: pointer;
  bottom: 80px;
  right: 20px;
  width: 40px;
  height: 40px;
  align-items: center;
  justify-content: center;
  color: white;
  z-index: ${zIndex.floatingChatButton};

  &:hover {
    background-color: ${(props) =>
      props.$showChannel ? colorSet.gray6 : colorSet.blue4};
  }
`;

const UnreadBadge = styled.span`
  position: absolute;
  top: -5px;
  right: -5px;
  background-color: ${colorSet.red2};
  color: white;
  border-radius: 10px;
  padding: 2px 6px;
  font-size: 11px;
  font-weight: 600;
  min-width: 18px;
  height: 18px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;

const ChannelModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: ${zIndex.floatingChatModal};
`;

const ChannelModalContent = styled.div<{
  $onlyList?: boolean;
  $withSidePanel?: boolean;
}>`
  position: absolute;
  width: ${({ $onlyList, $withSidePanel }) => {
    if ($onlyList) return "321px";
    if ($withSidePanel) return "min(calc(100vw - 40px), 1361px)"; // 321 + 720 + 320
    return "min(calc(100vw - 40px), 1041px)"; // 321 + 720
  }};
  height: 70vh;
  min-height: 400px;
  max-height: 700px;
  bottom: 140px;
  right: 20px;
  background: #fff;
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.15);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  transition: width 0.2s;
`;

const FlexRow = styled.div`
  display: flex;
  height: 100%;
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;

  &::-webkit-scrollbar {
    height: 8px;
  }

  &::-webkit-scrollbar-track {
    background: #f1f1f1;
  }

  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

const ChannelListSection = styled.div`
  flex: 0 0 321px;
  min-width: 321px;
  width: 321px;
  height: 100%;
  background: #fff;
  border-right: 1px solid #eee;
  overflow: hidden;
  display: flex;
  flex-direction: column;
`;

const ChannelSection = styled.div`
  flex: 0 0 720px;
  min-width: 720px;
  width: 720px;
  height: 100%;
  background: #fff;
  overflow: hidden;
  transition: width 0.2s;
`;

const SidePanelSection = styled.div`
  flex: 0 0 320px;
  min-width: 320px;
  width: 320px;
  height: 100%;
  background: #fff;
  overflow: hidden;
`;

// Moved to ChatInitializationStatus component for better separation of concerns
